import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { GraduationCap, Heart, DollarSign, TrendingUp, TrendingDown, BarChart3, Users, ArrowUpDown, Search } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

type TopicKey = "education-workforce" | "family-financial" | "health-childwelfare";

const TOPICS: { key: TopicKey; label: string; icon: React.ReactNode; description: string; color: string }[] = [
  {
    key: "education-workforce",
    label: "Education & Workforce",
    icon: <GraduationCap className="h-5 w-5" />,
    description: "Graduation rates, educational attainment, unemployment, and median income across Alabama's 67 counties.",
    color: "hsl(var(--primary))",
  },
  {
    key: "family-financial",
    label: "Family & Financial Well-Being",
    icon: <DollarSign className="h-5 w-5" />,
    description: "Poverty rates, SNAP participation, household income distribution, and family structure indicators.",
    color: "hsl(200, 70%, 50%)",
  },
  {
    key: "health-childwelfare",
    label: "Health & Child Welfare",
    icon: <Heart className="h-5 w-5" />,
    description: "Health insurance coverage, uninsured rates, child population, and single-parent household indicators.",
    color: "hsl(340, 65%, 50%)",
  },
];

const METRIC_CONFIGS: Record<TopicKey, { key: string; label: string; format: (v: number) => string; higherIsBetter: boolean }[]> = {
  "education-workforce": [
    { key: "bachelors_plus_pct", label: "Bachelor's Degree+", format: (v) => `${v}%`, higherIsBetter: true },
    { key: "hs_plus_pct", label: "HS Diploma+", format: (v) => `${v}%`, higherIsBetter: true },
    { key: "unemployment_rate", label: "Unemployment Rate", format: (v) => `${v}%`, higherIsBetter: false },
    { key: "median_household_income", label: "Median Income", format: (v) => `$${v.toLocaleString()}`, higherIsBetter: true },
  ],
  "family-financial": [
    { key: "poverty_rate", label: "Poverty Rate", format: (v) => `${v}%`, higherIsBetter: false },
    { key: "snap_rate", label: "SNAP Participation", format: (v) => `${v}%`, higherIsBetter: false },
    { key: "median_household_income", label: "Median Income", format: (v) => `$${v.toLocaleString()}`, higherIsBetter: true },
    { key: "median_home_value", label: "Median Home Value", format: (v) => `$${v.toLocaleString()}`, higherIsBetter: true },
    { key: "single_parent_pct", label: "Single-Parent HH", format: (v) => `${v}%`, higherIsBetter: false },
  ],
  "health-childwelfare": [
    { key: "uninsured_rate", label: "Uninsured Rate", format: (v) => `${v}%`, higherIsBetter: false },
    { key: "children_pct", label: "Children (% Pop)", format: (v) => `${v}%`, higherIsBetter: true },
    { key: "under_5", label: "Under 5 Pop.", format: (v) => v.toLocaleString(), higherIsBetter: true },
    { key: "single_parent_with_children", label: "Single-Parent w/ Kids", format: (v) => v.toLocaleString(), higherIsBetter: false },
  ],
};

const useCensusData = (topic: TopicKey) =>
  useQuery({
    queryKey: ["census-data", topic],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("census-data", {
        body: { topic },
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      return data as { data: Record<string, any>[]; source: string; count: number };
    },
    staleTime: 1000 * 60 * 30, // 30 min cache
  });

const DataTools = () => {
  const [activeTopic, setActiveTopic] = useState<TopicKey>("education-workforce");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortMetric, setSortMetric] = useState<string>("");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const { data: censusResult, isLoading, error } = useCensusData(activeTopic);

  const metrics = METRIC_CONFIGS[activeTopic];
  const activeMetricConfig = metrics.find((m) => m.key === (sortMetric || metrics[0].key)) || metrics[0];

  const filteredData = censusResult?.data
    ?.filter((c) => (c.county as string).toLowerCase().includes(searchQuery.toLowerCase()))
    ?.sort((a, b) => {
      const key = sortMetric || metrics[0].key;
      const va = typeof a[key] === "number" ? a[key] : 0;
      const vb = typeof b[key] === "number" ? b[key] : 0;
      return sortDir === "desc" ? vb - va : va - vb;
    }) || [];

  // Top chart data (top 15)
  const chartMetricKey = sortMetric || metrics[0].key;
  const chartData = [...filteredData].slice(0, 15).map((c) => ({
    county: c.county,
    value: typeof c[chartMetricKey] === "number" ? c[chartMetricKey] : 0,
  }));

  // Statewide averages
  const stateAvg = (key: string) => {
    if (!censusResult?.data?.length) return 0;
    const vals = censusResult.data.map((c) => (typeof c[key] === "number" ? (c[key] as number) : 0));
    return Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10;
  };

  const handleSort = (key: string) => {
    if (sortMetric === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortMetric(key);
      setSortDir("desc");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-[94px]">
        {/* Hero */}
        <section className="py-16 border-b border-border">
          <div className="container mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="h-4 w-4 text-primary" />
                <span className="text-primary text-sm font-semibold tracking-wider uppercase">Data Tools</span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">Alabama Community Data Explorer</h1>
              <p className="text-muted-foreground max-w-2xl text-lg">
                Hyperlocal data for all 67 Alabama counties — powered by U.S. Census Bureau American Community Survey estimates. Explore education, financial well-being, and health indicators at the county level.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Topic Tabs */}
        <section className="py-10">
          <div className="container mx-auto px-6">
            <Tabs value={activeTopic} onValueChange={(v) => { setActiveTopic(v as TopicKey); setSortMetric(""); setSearchQuery(""); }}>
              <TabsList className="grid grid-cols-1 md:grid-cols-3 h-auto gap-2 bg-transparent p-0 mb-8">
                {TOPICS.map((t) => (
                  <TabsTrigger
                    key={t.key}
                    value={t.key}
                    className="flex items-center gap-3 px-5 py-4 bg-card border border-border rounded-xl data-[state=active]:border-primary data-[state=active]:bg-primary/5 text-left justify-start h-auto"
                  >
                    <div className="shrink-0">{t.icon}</div>
                    <div>
                      <p className="font-semibold text-sm">{t.label}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{t.description}</p>
                    </div>
                  </TabsTrigger>
                ))}
              </TabsList>

              {TOPICS.map((topic) => (
                <TabsContent key={topic.key} value={topic.key}>
                  {isLoading ? (
                    <div className="text-center py-20">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
                      <p className="text-muted-foreground">Fetching live Census data for 67 counties…</p>
                    </div>
                  ) : error ? (
                    <div className="text-center py-20">
                      <p className="text-destructive mb-2">Failed to load data</p>
                      <p className="text-muted-foreground text-sm">{(error as Error).message}</p>
                    </div>
                  ) : (
                    <>
                      {/* Statewide Summary Cards */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        {metrics.map((m, i) => {
                          const avg = stateAvg(m.key);
                          return (
                            <motion.div
                              key={m.key}
                              initial={{ opacity: 0, y: 16 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.06 }}
                              className="bg-card border border-border rounded-xl p-5 cursor-pointer hover:border-primary/50 transition-colors"
                              onClick={() => handleSort(m.key)}
                            >
                              <p className="text-xs text-muted-foreground mb-1">{m.label} <span className="text-[10px]">(avg)</span></p>
                              <p className="font-display text-2xl font-bold">{m.format(avg)}</p>
                              <p className="text-[10px] text-muted-foreground mt-1">Click to sort · {censusResult?.count} counties</p>
                            </motion.div>
                          );
                        })}
                      </div>

                      {/* Chart */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-card border border-border rounded-xl p-5 mb-8"
                      >
                        <h3 className="font-display font-semibold text-lg mb-1">
                          Top 15 Counties — {activeMetricConfig.label}
                        </h3>
                        <p className="text-xs text-muted-foreground mb-4">
                          Sorted {sortDir === "desc" ? "highest" : "lowest"} first · {censusResult?.source}
                        </p>
                        <ResponsiveContainer width="100%" height={360}>
                          <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                            <YAxis dataKey="county" type="category" width={100} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                            <Tooltip
                              formatter={(value: number) => [activeMetricConfig.format(value), activeMetricConfig.label]}
                              contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                              labelStyle={{ color: 'hsl(var(--foreground))' }}
                            />
                            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                              {chartData.map((_, idx) => (
                                <Cell key={idx} fill={idx === 0 ? topic.color : `hsl(var(--primary) / ${0.7 - idx * 0.03})`} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </motion.div>

                      {/* Search + Table */}
                      <div className="mb-4 flex items-center gap-3">
                        <div className="relative flex-1 max-w-sm">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search counties…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">{filteredData.length} counties</p>
                      </div>

                      <div className="bg-card border border-border rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-border bg-muted/30">
                                <th className="text-left px-4 py-3 font-semibold text-xs text-muted-foreground">County</th>
                                {metrics.map((m) => (
                                  <th
                                    key={m.key}
                                    className="text-right px-4 py-3 font-semibold text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                                    onClick={() => handleSort(m.key)}
                                  >
                                    <div className="flex items-center justify-end gap-1">
                                      {m.label}
                                      <ArrowUpDown className="h-3 w-3" />
                                    </div>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredData.map((c, idx) => (
                                <tr key={c.fips} className={`border-b border-border/50 ${idx % 2 === 0 ? "" : "bg-muted/10"}`}>
                                  <td className="px-4 py-3 font-medium">{c.county}</td>
                                  {metrics.map((m) => {
                                    const val = typeof c[m.key] === "number" ? (c[m.key] as number) : 0;
                                    const avg = stateAvg(m.key);
                                    const isGood = m.higherIsBetter ? val > avg : val < avg;
                                    return (
                                      <td key={m.key} className="text-right px-4 py-3">
                                        <span className="font-mono text-sm">{m.format(val)}</span>
                                        <span className={`ml-1.5 text-[10px] ${isGood ? "text-primary" : "text-destructive"}`}>
                                          {isGood ? "▲" : "▼"}
                                        </span>
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <p className="text-[10px] text-muted-foreground mt-4 text-center">
                        Source: {censusResult?.source} · Data reflects estimates, not exact counts · ▲/▼ indicates above/below state average
                      </p>
                    </>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default DataTools;
